import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Redirect } from "react-router-dom";
import Moment from "moment";
import "react-datetime/css/react-datetime.css";
import Datetime from "react-datetime";

import { makeStyles } from "@material-ui/core/styles";
import Assignment from "@material-ui/icons/Assignment";
import {
  FormControl,
  Input,
  InputLabel,
  MenuItem,
  Select
} from "@material-ui/core";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardIcon from "components/Card/CardIcon.js";
import CardHeader from "components/Card/CardHeader.js";
import CustomInput from "components/CustomInput/CustomInput.js";
import AgentDialog from "components/AgentDialog/AgentDialog.js";

import { bookingApi, masterApi } from "functions/apis";

import styles from "assets/jss/material-dashboard-pro-react/views/formsStyle.js";

const useStyles = makeStyles(styles);

// Matches Sindo.Booking.Enums.AgentQuotaPolicyCriteriaType
const CRITERIA = [
  { value: 20, label: "Daily" },
  { value: 0, label: "Specific Date" },
  { value: 10, label: "Day Of Week" }
];
const DAYS = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" }
];

export default function CreateQuotaPolicy(props) {
  const source = useRef(new AbortController());

  const [agent, setAgent] = React.useState(null);
  const [showAgentModal, setShowAgentModal] = React.useState(false);
  const [routes, setRoutes] = React.useState([]);
  const [redirect, setRedirect] = React.useState(false);
  const [disabled, setDisabled] = React.useState(false);
  const [input, setInput] = React.useState({
    agentID: "",
    routeID: "",
    time: "",
    allocatedSeat: 0,
    criteriaType: 20,
    date: null,
    dayOfWeek: null,
    priority: 0
  });

  useEffect(() => {
    masterApi
      .get("/Routes", {
        signal: source.current.signal,
        params: {
          filter: { searchString: "", publicationStatus: 1, sort: 0 },
          pagination: { pageIndex: 0, pageSize: 0 }
        }
      })
      .then(data => setRoutes(data.records))
      .catch(error => console.log(error));

    return () => source.current.abort();
  }, []);

  const handleChange = field => e =>
    setInput({ ...input, [field]: e.target.value });
  const handleAgentSelected = e => {
    setShowAgentModal(false);
    setAgent(e);
    setInput({ ...input, agentID: e.id });
  };
  const handleNumberChange = field => e =>
    setInput({ ...input, [field]: parseInt(e.target.value || "0", 10) });

  const handleSubmit = e => {
    e.preventDefault();
    setDisabled(true);
    bookingApi
      .post(
        "/AgentQuotaPolicies",
        {
          ...input,
          date: input.date ? Moment(input.date).format("YYYY-MM-DD") : null
        },
        { signal: source.current.signal }
      )
      .then(() => setRedirect(true))
      .catch(error => {
        console.log(error);
        setDisabled(false);
      });
  };

  const classes = useStyles();
  return redirect ? (
    <Redirect to={"/admin/AgentQuotaPolicies"} />
  ) : (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="rose" icon>
            <CardIcon color="rose">
              <Assignment />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>CREATE QUOTA POLICY</h4>
          </CardHeader>
          <CardBody>
            <Button onClick={() => props.history.goBack()}>BACK</Button>
            <form onSubmit={handleSubmit}>
              <GridContainer>
                <GridItem xs={12} sm={6}>
                  <FormControl
                    fullWidth
                    className={classes.selectFormControl}
                    onClick={() =>
                      !showAgentModal ? setShowAgentModal(true) : null
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <InputLabel
                      className={classes.selectLabel}
                      style={{ pointerEvents: "none" }}
                    >
                      Agent
                    </InputLabel>
                    <Input
                      className={classes.select}
                      value={agent ? agent.code + " - " + agent.name : ""}
                      disabled
                      style={{ pointerEvents: "none" }}
                    />
                    <AgentDialog
                      openModal={showAgentModal}
                      setOpenModal={val => setShowAgentModal(val)}
                      onSelect={handleAgentSelected}
                    />
                  </FormControl>
                </GridItem>
                <GridItem xs={12} sm={3}>
                  <FormControl fullWidth className={classes.selectFormControl}>
                    <InputLabel className={classes.selectLabel}>
                      Route
                    </InputLabel>
                    <Select
                      value={input.routeID}
                      onChange={handleChange("routeID")}
                    >
                      {routes.map(r => (
                        <MenuItem value={r.id} key={r.id}>
                          <b>{r.code}</b>&nbsp;{r.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </GridItem>
                <GridItem xs={12} sm={3}>
                  <CustomInput
                    labelText="Time (hhmm, e.g. 0900)"
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      onChange: handleChange("time"),
                      value: input.time
                    }}
                  />
                </GridItem>
                <GridItem xs={12} sm={6}>
                  <CustomInput
                    labelText="Allocated Seat"
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      type: "number",
                      onChange: handleNumberChange("allocatedSeat"),
                      value: input.allocatedSeat
                    }}
                  />
                </GridItem>
                <GridItem xs={12} sm={6}>
                  <FormControl fullWidth className={classes.selectFormControl}>
                    <InputLabel className={classes.selectLabel}>
                      Criteria
                    </InputLabel>
                    <Select
                      value={input.criteriaType}
                      onChange={e =>
                        setInput({
                          ...input,
                          criteriaType: e.target.value,
                          date: null,
                          dayOfWeek: null
                        })
                      }
                    >
                      {CRITERIA.map(c => (
                        <MenuItem value={c.value} key={c.value}>
                          {c.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </GridItem>
                {input.criteriaType === 0 && (
                  <GridItem xs={12} sm={6}>
                    <FormControl fullWidth>
                      <Datetime
                        value={input.date}
                        onChange={e =>
                          setInput({ ...input, date: e === "" ? null : e })
                        }
                        timeFormat={false}
                        closeOnSelect={true}
                        inputProps={{ placeholder: "Date" }}
                      />
                    </FormControl>
                  </GridItem>
                )}
                {input.criteriaType === 10 && (
                  <GridItem xs={12} sm={6}>
                    <FormControl
                      fullWidth
                      className={classes.selectFormControl}
                    >
                      <InputLabel className={classes.selectLabel}>
                        Day Of Week
                      </InputLabel>
                      <Select
                        value={input.dayOfWeek ?? ""}
                        onChange={handleChange("dayOfWeek")}
                      >
                        {DAYS.map(d => (
                          <MenuItem value={d.value} key={d.value}>
                            {d.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </GridItem>
                )}
                <GridItem xs={12} sm={6}>
                  <CustomInput
                    labelText="Priority (lower = higher precedence)"
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      type: "number",
                      onChange: handleNumberChange("priority"),
                      value: input.priority
                    }}
                  />
                </GridItem>
              </GridContainer>
              <Button disabled={disabled} color="rose" type="submit">
                Submit
              </Button>
            </form>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}

CreateQuotaPolicy.propTypes = {
  history: PropTypes.object
};
